import { useState } from 'react'

import { initRequestParamWithDaylist } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import { addDays } from 'date-fns'
import _ from 'lodash'
import { useCurrentStatusStore } from 'store/currentStatusStore'
import { convertDateToRequestParam, dateToStr24HPad0DayOfWeek, normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'

import type { GetStockStockGetRequest, ResponseResult, ResponseUpdate, Stock, Stocks, StockPlanUpdateStockPlanUpdatePostRequest, ShipExclusionUpdateShipExclusionUpdatePostRequest, DlStockCsvDownloadCsvStockGetRequest, DlStockXlsxDownloadXlsxStockGetRequest, StockUpdateUploadStockPlanUploadPostRequest, OptimizeResultUpdateStockUpdatePostRequest, MstCenterStockItem } from 'api-client'
import type { ErrorResponse } from 'types/types'

/**
 * Extended Stock interface with additional properties added at runtime
 */
export interface StockExtended extends Stock {
  itemName?: string | null
  janCase?: string | null
  orderUnitTypeName?: string | null
  caseLot?: number | null
  minOrderQuantity?: number | null
  weight?: number | null
  maxPalletQuantity?: number | null
  supplierName?: string | null
  isAlertMaster?: boolean | null
  expectedRank?: number | null
}

export interface StockAdjust extends StockExtended {
  id: string
  systemDate: Date | null
  arrivalQuantityPrev: number | null
  shipQuantityPrev: number | null
  stockQuantityPrev: number | null
  orderQuantityReasonPrev: string | null
}

export interface StocksAdjust extends Stocks {
  list: StockAdjust[]
}

export const useStock = () => {
  const { loginUserInfo, typeCode } = useAuthContext()
  const [stock, setStock] = useState<StocksAdjust>({
    ...initRequestParamWithDaylist, 
    renewalOrgItemList: [], 
    transportLotList: [],
    optimizeRangeStartDate: null,
    optimizeRangeEndDate: null
  })
  const [stockData, setStockData] = useState<Stocks>({
    datacount: null,
    maxcount: null,
    lastUpdateDatetime: null,
    optimizeRangeStartDate: null,
    optimizeRangeEndDate: null,
    list: [],
    daylist: [],
    daylistCenterSupplier: [],
    renewalOrgItemList: [],
    transportLotList: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<ResponseResult | undefined>(undefined)
  const [stockUpdateResult, setStockUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const [isUpdating, setIsUpdating] = useState(false)
  const { setCurrentStatuses } = useCurrentStatusStore()
  const [isCsvLoading, setIsCsvLoading] = useState(false)
  const [isXlsxLoading, setIsXlsxLoading] = useState(false)

  const calcErraRate = (shipQuantityExpected: number | null, shipQuantityRec: number | null) => {
    if (shipQuantityRec == null || shipQuantityRec === 0) return null

    return (
      (shipQuantityExpected == null ? 0 : shipQuantityExpected - shipQuantityRec) /
      (shipQuantityRec === 0 ? 1 : shipQuantityRec)
    )
  }

  // Gen request params
  const generateRequestParams = (searchParam: GetStockStockGetRequest) => {
    const requestParams: GetStockStockGetRequest[] = []
    
    // If getPeriodDataFlg is true, just return the searchParam as is
    if (searchParam.getPeriodDataFlg) {
      requestParams.push({
        ...searchParam
      })
      return requestParams
    }
    const startInt = searchParam.startDateTypeCode ? Math.floor(Number(searchParam.startDateTypeCode) / 7) : -1
    const endInt = searchParam.endDateTypeCode ? Math.ceil(Number(searchParam.endDateTypeCode) / 7 + 2) : 4
        
    if ((searchParam.centerId || searchParam.centerIds?.length || searchParam.orgCenterId) && searchParam.itemCode) {
      requestParams.push({
        ...searchParam
      })
    } else {
      if (!loginUserInfo?.systemDate) return requestParams
      const systemDate = convertDateToRequestParam(loginUserInfo?.systemDate)
      
      if (!searchParam.dateRange) {
        for (let i = startInt; i < endInt; i += 1) {
          requestParams.push({
            ...searchParam,
            startDate: addDays(systemDate, i * 7),
            endDate: addDays(systemDate, i * 7 + 6),
            dateRange: 'week',
            startDateTypeCode: undefined,
            endDateTypeCode: undefined,
          })
        }
      } else {
        requestParams.push({
          ...searchParam
        })
      }
    }
    
    return requestParams
  }

  // Merge into single response
  const mergeResponses = (responses: any[], centerStockItemData: MstCenterStockItem[]): { data: Stocks; result: ResponseResult } => {
    const failedResponse = responses.find(res => res.result.status !== 'success')
    const mergedResult = failedResponse ? failedResponse.result : responses[0].result
    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)

    const defaultSafetyStockSetting = typeCode
      .find(group => group.typeGroup === 'safety_stock_default')?.codes
      .find(code => code.defaultFlg)?.typeCode

    const defaultStockDaysSetting = typeCode
      .find(group => group.typeGroup === 'stock_days_default')?.codes
      .find(code => code.defaultFlg)?.typeCode

    const sortByOrgAndDate = (a: any, b: any) => {
      if (a.orgCenterId !== b.orgCenterId) {
        return (a.orgCenterId || '').localeCompare(b.orgCenterId || '')
      }
      if (a.orgItemCode !== b.orgItemCode) {
        return (a.orgItemCode || '').localeCompare(b.orgItemCode || '')
      }
      const dateA = a.recDate ? new Date(a.recDate).getTime() : 0
      const dateB = b.recDate ? new Date(b.recDate).getTime() : 0
      return dateA - dateB
    }

    const daylist = responses
      .flatMap(res => res.data.daylist)
      .sort((a, b) => {
        const dateA = a.datevalue ? new Date(a.datevalue).getTime() : 0
        const dateB = b.datevalue ? new Date(b.datevalue).getTime() : 0
        return dateA - dateB
      })
      .map((item, index) => ({
        ...item,
        dateno: index + 1,
        colname: `col${index + 1}`
      }))

    const list = responses
      .flatMap(res => res.data.list)
      .sort(sortByOrgAndDate)
      .map(item => {
        const daylistItem = daylist.find(day => 
          day.datevalue && item.recDate && 
          new Date(day.datevalue).toDateString() === new Date(item.recDate).toDateString()
        )

        const centerStockItem = centerStockItemData.find(centerItem => 
          centerItem.companyId === item.companyId &&
          centerItem.centerId === item.centerId &&
          centerItem.orgItemCode === item.orgItemCode
        )
        
        return {
          ...item,
          dateno: daylistItem?.dateno || item.dateno,
          colname: daylistItem?.colname || item.colname,
          // Map centerStockItemData fields to item
          expectedRank: centerStockItem?.expectedRank,
          itemName: centerStockItem?.mstItemItemName || item.itemName,
          itemCode: centerStockItem?.itemCode || item.itemCode,
          supplierName: centerStockItem?.supplierName ?? null,
          orderUnitTypeName: centerStockItem?.orderUnitTypeName ?? null,
          caseLot: centerStockItem?.trCaseLot ?? null,
          minOrderQuantity: centerStockItem?.trMinOrderQuantity ?? null,
          weight: centerStockItem?.mstItemWeight ?? null,
          maxPalletQuantity: centerStockItem?.mstItemMaxPalletQuantity ?? null,
          stockDaysSetting: (centerStockItem?.trStockDaysSetting == null && centerStockItem?.minStockQuantity == null) ? Number(defaultStockDaysSetting) : (centerStockItem?.trStockDaysSetting ?? null),
          minStockQuantity: centerStockItem?.minStockQuantity ?? null,
          safetyStockSetting: centerStockItem?.trSafetyStockSetting != null ? Number(centerStockItem.trSafetyStockSetting) : Number(defaultSafetyStockSetting),
          startDate: centerStockItem?.trStartDate ?? null,
          stopShip: centerStockItem?.stopShip ?? null,
          stopArrival: centerStockItem?.trStopArrival ?? null,
          orderUnitType: centerStockItem?.trOrderUnitType ?? null,
          isAlertMaster: centerStockItem?.isAlertMaster ?? null,
          isMinShipDateAlert: centerStockItem?.isMinShipDateAlert ?? null,
          isEndSaleAlert: centerStockItem?.isEndSaleAlert ?? null,
          isInactive: centerStockItem?.isInactive ?? null,
          isExpectedRankAlert: centerStockItem?.isExpectedRankAlert ?? null,
        }
      })

    const mergedData: Stocks = {
      datacount: responses.reduce((sum, res) => (sum || 0) + (res.data.datacount || 0), 0),
      maxcount: responses.reduce((sum, res) => (sum || 0) + (res.data.maxcount || 0), 0),
      lastUpdateDatetime: responses.reduce((latest, res) => {
        if (!res.data.lastUpdateDatetime) return latest
        if (!latest) return res.data.lastUpdateDatetime
        return new Date(res.data.lastUpdateDatetime) > new Date(latest) 
          ? res.data.lastUpdateDatetime 
          : latest
      }, null),
      optimizeRangeStartDate: responses[0]?.data?.optimizeRangeStartDate || null,
      optimizeRangeEndDate: responses[0]?.data?.optimizeRangeEndDate || null,
      list,
      daylist,
      daylistCenterSupplier: responses.flatMap(res => res.data.daylistCenterSupplier || []),
      renewalOrgItemList: responses.flatMap(res => res.data.renewalOrgItemList).sort(sortByOrgAndDate),
      transportLotList: responses.flatMap(res => res.data.transportLotList).sort(sortByOrgAndDate)
    }
    
    return { data: mergedData, result: mergedResult }
  }

  const fetchStockData = async (searchParam: GetStockStockGetRequest, centerStockItemFlg = true) => {
    setResult(undefined)
    setIsLoading(true)

    try {
      const requestParams = generateRequestParams(searchParam)
      const api = await getAPI()
      if (!api) return undefined

      let centerStockItemData: MstCenterStockItem[] | undefined
      if (centerStockItemFlg) {
        const centerStockItemResponse = await api.getMstCenterStockItemCenterStockItemGet({
          companyId: searchParam.companyId,
          centerIds: searchParam.centerIds && searchParam.centerIds.length > 0 ? searchParam.centerIds : undefined,
          orgSupplierIds: searchParam.orgSupplierIds && searchParam.orgSupplierIds.length > 0 ? searchParam.orgSupplierIds : undefined,
          orgSupplierId: searchParam.orgSupplierId || undefined,
          searchTargetEnabled: 'off_delete',
          enabled: true,
        })
        centerStockItemData = centerStockItemResponse.data.list || []
      }

      const promises = requestParams.map(param => {
        // When multiple centers selected, use centerIds and don't send centerId
        const hasMultipleCenters = param.centerIds && param.centerIds.length > 1
        // Determine centerId: undefined if multiple, first of centerIds if single, otherwise param.centerId
        const getEffectiveCenterId = (): number | undefined => {
          if (hasMultipleCenters) return undefined
          if (param.centerIds?.length === 1) return param.centerIds[0]
          return param.centerId || undefined
        }
        return api.getStockStockGet({
          ...param,
          companyId: param.companyId,
          centerId: getEffectiveCenterId(),
          centerIds: param.centerIds || undefined,
          orgSupplierId: param.orgSupplierId || undefined,
          orderPlanFlg: param.orderPlanFlg || undefined,
          sort: param.sort || undefined,
        })
      })

      const responses = await Promise.all(promises)
      
      const { data: mergedData, result: mergedResult } = mergeResponses(responses, centerStockItemData || [])
      
      setStockData(mergedData)
      setCurrentStatuses(mergedResult.systemInfo?.sysCurrentStatus)
      setResult({
        status: 'success',
        message: '',
        systemDate: mergedResult.systemDate,
        pageNo: mergedResult.pageNo,
        systemInfo: mergedResult.systemInfo,
      })
      return mergedData
    } catch (e) {
      const error = e as ErrorResponse
      setResult({
        status: 'error',
        message:
          error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `在庫データの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStock = async (searchParam: GetStockStockGetRequest) => {
    setStock({
      ...initRequestParamWithDaylist, 
      renewalOrgItemList: [], 
      transportLotList: [],
      optimizeRangeStartDate: null,
      optimizeRangeEndDate: null
    })
    setResult(undefined)
    setIsLoading(true)

    const defaultSafetyStockSetting = typeCode
    .find(group => group.typeGroup === 'safety_stock_default')?.codes
    .find(code => code.defaultFlg)?.typeCode

  const defaultStockDaysSetting = typeCode
    .find(group => group.typeGroup === 'stock_days_default')?.codes
    .find(code => code.defaultFlg)?.typeCode

    try {
      const api = await getAPI()
      if (!api) return undefined

      const centerStockItemResponse = await api.getMstCenterStockItemCenterStockItemGet({
        companyId: searchParam.companyId,
        centerIds: searchParam.centerIds && searchParam.centerIds.length > 0 ? searchParam.centerIds : undefined,
        centerId: searchParam.centerId || undefined,
        orgSupplierId: searchParam.orgSupplierId || undefined,
        orgSupplierIds: searchParam.orgSupplierIds && searchParam.orgSupplierIds.length > 0 ? searchParam.orgSupplierIds : undefined,
        searchTargetEnabled: 'off_delete',
        enabled: true,
      })
      const centerStockItemData = centerStockItemResponse.data.list ?? []
      
      const response = await api.getStockStockGet({
        ...searchParam,
        companyId: searchParam.companyId,
        centerId: searchParam.centerId || undefined,
        orgSupplierId: searchParam.orgSupplierId || undefined,
        itemCode: searchParam.itemCode || undefined,
        sort: searchParam.sort || undefined,
      })

      setStock({
        ...response.data,
        list: _.map(response.data.list, (value) => {
          const recDate = new Date(value.recDate as Date)

          const centerStockItem = centerStockItemData.find(centerItem => 
            centerItem.companyId === value.companyId &&
            centerItem.centerId === value.centerId &&
            centerItem.orgItemCode === value.orgItemCode
          )
          return {
            ...value,
            // 用途:ダッシュボードページ
            // rechartsライブラリのdataKeyに合わせてnumberにする
            recDateToNumber: recDate.getTime(),
            errorRate: calcErraRate(value.shipQuantityExpected, value.shipQuantityRec),

            // 用途:入荷数修正ページ
            id: Math.random().toString(32).substring(2),
            systemDate: response.result.systemDate,
            arrivalQuantityPrev: value.arrivalQuantity,
            shipQuantityPrev: value.shipQuantity,
            stockQuantityPrev: value.stockQuantity,
            stockDaysPrev: value.stockDays,
            // DataGridのセレクターの型がstringのため、nullの場合は空文字を返す
            expectedRank: centerStockItem?.expectedRank,
            orderQuantityReasonPrev: value.orderQuantityReason ?? '',
            orderQuantityReason: value.orderQuantityReason ?? '',
            shipQuantityInterpolated: value.shipQuantityInterpolated,
            itemName: centerStockItem?.mstItemItemName || value.itemName,
            itemCode: centerStockItem?.itemCode || value.itemCode,
            supplierName: centerStockItem?.supplierName ?? null,
            orderUnitTypeName: centerStockItem?.orderUnitTypeName ?? null,
            caseLot: centerStockItem?.trCaseLot ?? null,
            minOrderQuantity: centerStockItem?.trMinOrderQuantity ?? null,
            weight: centerStockItem?.mstItemWeight ?? null,
            maxPalletQuantity: centerStockItem?.mstItemMaxPalletQuantity ?? null,
            stockDaysSetting: (centerStockItem?.trStockDaysSetting == null && centerStockItem?.minStockQuantity == null) ? Number(defaultStockDaysSetting) : (centerStockItem?.trStockDaysSetting ?? null),
            minStockQuantity: centerStockItem?.minStockQuantity ?? null,
            safetyStockSetting: centerStockItem?.trSafetyStockSetting != null ? Number(centerStockItem.trSafetyStockSetting) : Number(defaultSafetyStockSetting),
            startDate: centerStockItem?.trStartDate ?? null,
            stopShip: centerStockItem?.stopShip ?? null,
            stopArrival: centerStockItem?.trStopArrival ?? null,
            orderUnitType: centerStockItem?.trOrderUnitType ?? null,
            itemMaxPalletQuantity: centerStockItem?.mstItemMaxPalletQuantity ?? null,
          }
        }),
      })
      setResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `在庫データの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsLoading(false)
    }
  }


  // 製品のアップデートに使用されます
  const fetchUpdateStock = async (body: StockPlanUpdateStockPlanUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setStockUpdateResult(undefined)
    setResult(undefined)
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.stockPlanUpdateStockPlanUpdatePost(body);
      
      setResult(response.result)
      setStockUpdateResult(response);
      
      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '入力エラーがあります');
        Object.assign(error, {
            ...response
        });
        throw error;
      }
      return response;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'result' in error) {
          const apiResponse = error as ResponseUpdate; 
          await new Promise<void>(resolve => {
            setStockUpdateResult(apiResponse);
            resolve();
          });
          return await Promise.reject(apiResponse);
      }
      const { response } = error as any;
      const errorResponse = {
          errors: [],
          result: {
              status: 'error',
              message: response && response.status === 422 ? '入力値に誤りがあります。' : `在庫データの更新でエラーが発生しました。`,
              systemDate: null,
              pageNo: 0,
              systemInfo: null,
          },
          updateCount: null,
          lastUpdateDatetime: null,
      };
      await new Promise<void>(resolve => {
          setStockUpdateResult(errorResponse);
          setResult(errorResponse.result)
          resolve();
      });
      return await Promise.reject(errorResponse);
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchUpdateShipExclusion = async (body: ShipExclusionUpdateShipExclusionUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setStockUpdateResult(undefined)
    setResult(undefined)
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.shipExclusionUpdateShipExclusionUpdatePost(body);
      
      setResult(response.result)
      setStockUpdateResult(response);
      
      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '出荷除外データの更新でエラーが発生しました。');
        Object.assign(error, {
            ...response
        });
        throw error;
      }
      return response;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'result' in error) {
          const apiResponse = error as ResponseUpdate; 
          await new Promise<void>(resolve => {
            setStockUpdateResult(apiResponse);
            resolve();
          });
          return await Promise.reject(apiResponse);
      }
      const { response } = error as any;
      const errorResponse = {
          errors: [],
          result: {
              status: 'error',
              message: response && response.status === 422 ? '入力値に誤りがあります。' : `出荷除外データの更新でエラーが発生しました。`,
              systemDate: null,
              pageNo: 0,
              systemInfo: null,
          },
          updateCount: null,
          lastUpdateDatetime: null,
      };
      await new Promise<void>(resolve => {
          setStockUpdateResult(errorResponse);
          setResult(errorResponse.result)
          resolve();
      });
      return await Promise.reject(errorResponse);
    } finally {
      setIsUpdating(false);
    }
  }

  const fetchUpdateStockCenter = async (body: OptimizeResultUpdateStockUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setStockUpdateResult(undefined)
    setResult(undefined)
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.optimizeResultUpdateStockUpdatePost(body);
      
      setResult(response.result)
      setStockUpdateResult(response);
      return response;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'result' in error) {
          const apiResponse = error as ResponseUpdate; 
          await new Promise<void>(resolve => {
            setStockUpdateResult(apiResponse);
            resolve();
          });
          return await Promise.reject(apiResponse);
      }
      const { response } = error as any;
      const errorResponse = {
          errors: [],
          result: {
              status: 'error',
              message: response && response.status === 422 ? '入力値に誤りがあります。' : `発注推奨数の更新でエラーが発生しました。`,
              systemDate: null,
              pageNo: 0,
              systemInfo: null,
          },
          updateCount: null,
          lastUpdateDatetime: null,
      };
      await new Promise<void>(resolve => {
          setStockUpdateResult(errorResponse);
          setResult(errorResponse.result)
          resolve();
      });
      return await Promise.reject(errorResponse);
    } finally {
      setIsUpdating(false);
    }
  }

  const fetchStockCsvDownload = async (filename: string, searchParam: DlStockCsvDownloadCsvStockGetRequest) => {
    setIsCsvLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.dlStockCsvDownloadCsvStockGetRaw(searchParam)

      const contentType = response.raw.headers.get('content-type') ?? ''
      if (contentType.includes('application/json')) {
        const json = await response.raw.json()
        if (json?.result?.status !== 'success') {
          const downloadResult: ResponseResult = {
            status: json?.result?.status ?? 'error',
            message: json?.result?.message ?? '在庫CSVダウンロードでエラーが発生しました。',
            systemDate: null,
            pageNo: 0,
            systemInfo: null,
          }
          setResult(downloadResult)
          return downloadResult
        }
        return undefined
      }

      const blob = await response.raw.blob()
      const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${filename}_${dateToStr24HPad0DayOfWeek(new Date(), 'YYYYMMDDhhmmss')}`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)

      return undefined
    } catch (error) {
      const downloadResult: ResponseResult = {
        status: 'error',
        message: '在庫CSVダウンロードでエラーが発生しました。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(downloadResult)
      return undefined
    } finally {
      setIsCsvLoading(false)
    }
  }

  const fetchStockXlsxDownload = async (filename: string, searchParam: DlStockXlsxDownloadXlsxStockGetRequest) => {
    setIsXlsxLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.dlStockXlsxDownloadXlsxStockGetRaw(searchParam)

      const contentType = response.raw.headers.get('content-type') ?? ''
      if (contentType.includes('application/json')) {
        const json = await response.raw.json()
        if (json?.result?.status !== 'success') {
          const downloadResult: ResponseResult = {
            status: json?.result?.status ?? 'error',
            message: json?.result?.message ?? '在庫Excelダウンロードでエラーが発生しました。',
            systemDate: null,
            pageNo: 0,
            systemInfo: null,
          }
          setResult(downloadResult)
          return downloadResult
        }
        return undefined
      }

      const blob = await response.raw.blob()
      const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${filename}_${dateToStr24HPad0DayOfWeek(new Date(), 'YYYYMMDDhhmmss')}`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)

      return undefined
    } catch (error) {
      const errorResult: ResponseResult = {
        status: 'error',
        message: '在庫Excelダウンロードでエラーが発生しました。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      return undefined
    } finally {
      setIsXlsxLoading(false)
    }
  }

  const fetchStockUploadFile = async (searchParam: StockUpdateUploadStockPlanUploadPostRequest) => {
    setIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.stockUpdateUploadStockPlanUploadPost(searchParam)
      setResult(response.result)
      return response
    } catch (error) {
      const errorResult: ResponseResult = {
        status: 'error',
        message: `在庫データのアップロードでエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      return { result: errorResult }
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStockUpload = async (searchParam: StockPlanUpdateStockPlanUpdatePostRequest) => {
    setIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.stockPlanUpdateStockPlanUpdatePost(searchParam)
      return response
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  return {
    result,
    stock,
    stockData,
    fetchStock,
    fetchStockData,
    isLoading,
    setIsLoading,

    isUpdating, 
    setStockUpdateResult,
    fetchUpdateStock,
    fetchUpdateShipExclusion,
    fetchUpdateStockCenter,

    fetchStockCsvDownload,
    isCsvLoading,
    fetchStockXlsxDownload,
    isXlsxLoading,
    fetchStockUploadFile,

    fetchStockUpload,
  }
}
